import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { refreshPlanAccess } from "../hooks/usePlanAccess";
import {
  Users, UserPlus, Trash2, RefreshCw, Crown, ShieldCheck, User as UserIcon,
  Copy, Clock, CheckCircle2, Lock, BarChart3,
} from "lucide-react";

const STATUS_STYLE = {
  accepted: { bg: "#dcfce7", color: "#16a34a", label: "Active", Icon: CheckCircle2 },
  pending: { bg: "#fef3c7", color: "#b45309", label: "Invited", Icon: Clock },
};

const ROLE_STYLE = {
  owner: { bg: "#ede9fe", color: "#6d28d9", label: "Owner", Icon: Crown },
  admin: { bg: "#dbeafe", color: "#1d4ed8", label: "Admin", Icon: ShieldCheck },
  member: { bg: "#f1f5f9", color: "#475569", label: "Member", Icon: UserIcon },
};

const Badge = ({ map, keyName }) => {
  const s = map[keyName] || map.member || map.pending;
  const Icon = s.Icon;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: "5px", background: s.bg,
      color: s.color, fontSize: "11px", fontWeight: 700, padding: "3px 10px", borderRadius: "12px",
    }}>
      {Icon && <Icon size={12} />} {s.label}
    </span>
  );
};

export default function Team() {
  const navigate = useNavigate();
  const [team, setTeam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [inviting, setInviting] = useState(false);
  const [form, setForm] = useState({ email: "", full_name: "", role: "member" });
  const [lastInvite, setLastInvite] = useState(null); // { email, temp_password }
  const [activity, setActivity] = useState(null);
  const [tab, setTab] = useState("members");

  const loadTeam = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/team`);
      setTeam(res.data);
      if (res.data.can_manage) {
        try {
          const act = await axios.get(`${API}/team/activity`);
          setActivity(act.data);
        } catch (e) { /* non-fatal */ }
      }
    } catch (e) {
      toast.error("Failed to load team");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    if (sessionId && params.get("type") === "team_addon") {
      window.history.replaceState({}, "", "/team");
      axios.get(`${API}/billing/checkout/status/${sessionId}`)
        .then(() => toast.success("Team add-on activated — invite your teammates below"))
        .catch(() => toast.error("We couldn't confirm the payment yet. Refresh in a moment."))
        .finally(() => { loadTeam(); refreshPlanAccess(); });
    } else {
      loadTeam();
    }
  }, [loadTeam]);

  const [addonBusy, setAddonBusy] = useState(false);
  const handleAddon = async () => {
    setAddonBusy(true);
    try {
      const res = await axios.post(`${API}/billing/checkout/team-addon`, { origin_url: window.location.origin });
      if (res.data.checkout_url) window.location.href = res.data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start checkout");
      setAddonBusy(false);
    }
  };

  const handleCancelAddon = async () => {
    if (!window.confirm("Cancel the Team add-on? Your teammates keep access until the end of this billing period.")) return;
    try {
      await axios.post(`${API}/billing/team-addon/cancel`);
      toast.success("Team add-on will end at the close of this billing period");
      loadTeam();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not cancel the add-on");
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!form.email) { toast.error("Please enter an email"); return; }
    setInviting(true);
    try {
      const res = await axios.post(`${API}/team/invite`, form);
      setLastInvite({ email: res.data.email, temp_password: res.data.temp_password });
      toast.success(`Invitation sent to ${res.data.email}`);
      setForm({ email: "", full_name: "", role: "member" });
      loadTeam();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send invite");
    } finally {
      setInviting(false);
    }
  };

  const handleResend = async (username, email) => {
    try {
      const res = await axios.post(`${API}/team/members/${username}/resend`);
      setLastInvite({ email, temp_password: res.data.temp_password });
      toast.success(`New temporary password generated for ${email}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to resend");
    }
  };

  const handleRemove = async (username, email) => {
    if (!window.confirm(`Remove ${email} from the team? They will lose access immediately.`)) return;
    try {
      await axios.delete(`${API}/team/members/${username}`);
      toast.success(`${email} removed from team`);
      loadTeam();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to remove member");
    }
  };

  const copyPassword = (pw) => {
    navigator.clipboard?.writeText(pw);
    toast.success("Temporary password copied");
  };

  if (loading) {
    return <div className="loading" data-testid="team-loading"><div className="spinner"></div></div>;
  }

  const canManage = team?.can_manage;
  const eligible = team?.eligible;
  const seatsFull = team && team.seats_remaining <= 0;

  return (
    <div data-testid="team-page">
      <div className="page-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: "12px" }}>
        <div>
          <h1 className="page-title" style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <Users size={26} /> Team
          </h1>
          <p className="page-description">
            {canManage
              ? "Invite teammates to share your leads, scans and pipeline."
              : `You're a team member on ${team?.owner?.full_name || team?.owner?.username}'s account.`}
          </p>
        </div>
        {eligible && (
          <div data-testid="team-seats-indicator" style={{
            background: "linear-gradient(135deg, #ede9fe 0%, #dbeafe 100%)",
            border: "1px solid #c4b5fd", borderRadius: "12px", padding: "12px 20px", textAlign: "right",
          }}>
            <div style={{ fontSize: "11px", color: "#64748b", marginBottom: "2px" }}>Seats used</div>
            <div style={{ fontSize: "18px", fontWeight: 700, color: "#6d28d9" }}>
              {team.seats_used} / {team.seat_limit}
            </div>
            <div style={{ fontSize: "11px", color: "#64748b", marginTop: "2px" }}>{team.plan_name} plan</div>
          </div>
        )}
      </div>

      {/* Not eligible: add-on or upgrade prompt (owner on a plan without team seats) */}
      {!eligible && canManage && (
        <div className="content-card" data-testid="team-upgrade-card" style={{ textAlign: "center", padding: "48px 24px" }}>
          <Lock size={40} style={{ color: "#8b5cf6", margin: "0 auto 16px" }} />
          <h2 className="card-title" style={{ marginBottom: "8px" }}>Bring your team on board</h2>
          <p className="page-description" style={{ maxWidth: "500px", margin: "0 auto 20px" }}>
            {team?.team_addon_eligible
              ? <>Add the <strong>Team add-on</strong> to your {team.plan_name} plan for <strong>${team.team_addon_price}/month</strong> and invite up to {team.team_addon_seats} teammates who share your leads and pipeline. Or upgrade to <strong>Growth</strong> (3 seats included) or <strong>Scale</strong> (10 seats).</>
              : <>Teams are available on paid plans. Subscribe to <strong>Basic</strong> or <strong>Starter</strong> and add {team?.team_addon_seats || 3} teammates for <strong>${team?.team_addon_price || 50}/month</strong>, or choose <strong>Growth</strong> (3 seats) or <strong>Scale</strong> (10 seats).</>}
          </p>
          <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
            {team?.team_addon_eligible && (
              <Button data-testid="team-addon-btn" onClick={handleAddon} disabled={addonBusy}
                style={{ background: "linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%)" }}>
                {addonBusy ? "Opening checkout…" : `Add Team · $${team.team_addon_price}/mo`}
              </Button>
            )}
            <Button data-testid="team-upgrade-btn" variant={team?.team_addon_eligible ? "outline" : "default"} onClick={() => navigate("/plans")}
              style={team?.team_addon_eligible ? {} : { background: "linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%)" }}>
              {team?.team_addon_eligible ? "Compare plans" : "View Plans"}
            </Button>
          </div>
        </div>
      )}

      {eligible && team?.team_addon && team?.my_role === "owner" && (
        <div data-testid="team-addon-active" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10, background: "#f5f3ff", border: "1px solid #ddd6fe", borderRadius: 12, padding: "12px 16px", marginBottom: 16, fontSize: 14, color: "#5b21b6" }}>
          <span>Team add-on active · {team.team_addon_seats} seats for ${team.team_addon_price}/month{team.team_addon_canceling ? " · ends at the close of this billing period" : ""}</span>
          {!team.team_addon_canceling && <Button variant="outline" size="sm" onClick={handleCancelAddon} data-testid="team-addon-cancel-btn">Cancel add-on</Button>}
        </div>
      )}

      {/* Invite form */}
      {eligible && canManage && (
        <div className="content-card" data-testid="team-invite-card">
          <div className="card-header">
            <h2 className="card-title" style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <UserPlus size={18} /> Invite a team member
            </h2>
          </div>
          {seatsFull && (
            <p className="form-help" style={{ color: "#b45309", marginBottom: "12px" }}>
              You've used all {team.seat_limit} seats. Remove a member or upgrade your plan to add more.
            </p>
          )}
          <form onSubmit={handleInvite}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "16px" }}>
              <div className="form-group">
                <label className="form-label">Email address</label>
                <input className="form-input" type="email" placeholder="teammate@company.com"
                  data-testid="invite-email-input" value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })} disabled={seatsFull} />
              </div>
              <div className="form-group">
                <label className="form-label">Full name (optional)</label>
                <input className="form-input" type="text" placeholder="Jane Smith"
                  data-testid="invite-name-input" value={form.full_name}
                  onChange={(e) => setForm({ ...form, full_name: e.target.value })} disabled={seatsFull} />
              </div>
              <div className="form-group">
                <label className="form-label">Role</label>
                <select className="form-select" data-testid="invite-role-select" value={form.role}
                  onChange={(e) => setForm({ ...form, role: e.target.value })} disabled={seatsFull || team.my_role !== "owner"}>
                  <option value="member">Member — full shared access</option>
                  {team.my_role === "owner" && <option value="admin">Admin — can manage members</option>}
                </select>
              </div>
            </div>
            <Button type="submit" data-testid="send-invite-btn" disabled={inviting || seatsFull}
              style={{ marginTop: "8px", background: "linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%)" }}>
              {inviting ? "Sending…" : "Send Invitation"}
            </Button>
          </form>

          {lastInvite && (
            <div data-testid="invite-credentials" style={{
              marginTop: "20px", background: "#f8fafc", border: "1px dashed #c4b5fd",
              borderRadius: "10px", padding: "16px",
            }}>
              <div style={{ fontSize: "13px", fontWeight: 600, color: "#334155", marginBottom: "6px" }}>
                Invitation emailed to {lastInvite.email}
              </div>
              <p className="form-help" style={{ marginBottom: "10px" }}>
                We've emailed them a temporary password via your CRM. If they don't receive it, share this temporary password with them directly — they'll set their own on first login:
              </p>
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <code style={{ background: "#1e293b", color: "#a5f3fc", padding: "8px 14px", borderRadius: "8px", fontSize: "14px", letterSpacing: "1px" }}>
                  {lastInvite.temp_password}
                </code>
                <Button variant="outline" size="sm" data-testid="copy-temp-password-btn" onClick={() => copyPassword(lastInvite.temp_password)}>
                  <Copy size={14} style={{ marginRight: "4px" }} /> Copy
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Tabs (managers only) */}
      {canManage && (eligible || team?.seats_used > 0) && (
        <div style={{ display: "flex", gap: "8px", marginBottom: "16px" }} data-testid="team-tabs">
          {[["members", "Members"], ["activity", "Activity"]].map(([key, label]) => (
            <button key={key} data-testid={`team-tab-${key}`} onClick={() => setTab(key)}
              style={{
                padding: "8px 18px", borderRadius: "10px", fontSize: "14px", fontWeight: 600, cursor: "pointer",
                border: tab === key ? "1px solid #7c3aed" : "1px solid #e2e8f0",
                background: tab === key ? "linear-gradient(135deg, #ede9fe 0%, #dbeafe 100%)" : "#fff",
                color: tab === key ? "#6d28d9" : "#64748b",
              }}>
              {label === "Activity" && <BarChart3 size={14} style={{ marginRight: "6px", verticalAlign: "-2px" }} />}
              {label}
            </button>
          ))}
        </div>
      )}

      {/* Members list */}
      {(!canManage || tab === "members") && (eligible || team?.seats_used > 0 || !canManage) && (
      <div className="content-card" data-testid="team-members-card">
        <div className="card-header">
          <h2 className="card-title">Members ({(team?.members?.length || 0) + 1})</h2>
        </div>
        <div style={{ overflowX: "auto" }}>
          <table className="data-table" style={{ width: "100%" }} data-testid="team-members-table">
            <thead>
              <tr>
                <th style={{ textAlign: "left" }}>Member</th>
                <th style={{ textAlign: "left" }}>Role</th>
                <th style={{ textAlign: "left" }}>Status</th>
                {canManage && <th style={{ textAlign: "right" }}>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {/* Owner row */}
              <tr data-testid="team-row-owner">
                <td>
                  <div style={{ fontWeight: 600, color: "#0f172a" }}>{team?.owner?.full_name || team?.owner?.username}</div>
                  <div style={{ fontSize: "12px", color: "#64748b" }}>{team?.owner?.email}</div>
                </td>
                <td><Badge map={ROLE_STYLE} keyName="owner" /></td>
                <td><Badge map={STATUS_STYLE} keyName="accepted" /></td>
                {canManage && <td></td>}
              </tr>
              {(team?.members || []).map((m) => (
                <tr key={m.username} data-testid={`team-row-${m.username}`}>
                  <td>
                    <div style={{ fontWeight: 600, color: "#0f172a" }}>{m.full_name || m.username}</div>
                    <div style={{ fontSize: "12px", color: "#64748b" }}>{m.email}</div>
                  </td>
                  <td><Badge map={ROLE_STYLE} keyName={m.team_role} /></td>
                  <td><Badge map={STATUS_STYLE} keyName={m.status} /></td>
                  {canManage && (
                    <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                      <Button variant="outline" size="sm" data-testid={`resend-${m.username}`}
                        onClick={() => handleResend(m.username, m.email)} style={{ marginRight: "8px" }}>
                        <RefreshCw size={13} style={{ marginRight: "4px" }} /> Resend
                      </Button>
                      <Button variant="outline" size="sm" data-testid={`remove-${m.username}`}
                        onClick={() => handleRemove(m.username, m.email)}
                        style={{ color: "#dc2626", borderColor: "#fecaca" }}>
                        <Trash2 size={13} />
                      </Button>
                    </td>
                  )}
                </tr>
              ))}
              {(!team?.members || team.members.length === 0) && (
                <tr>
                  <td colSpan={canManage ? 4 : 3} style={{ textAlign: "center", color: "#94a3b8", padding: "24px" }}>
                    No team members yet{canManage ? " — invite your first teammate above." : "."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      )}

      {/* Activity view (managers only) */}
      {canManage && tab === "activity" && (
        <>
          <div className="content-card" data-testid="team-activity-summary">
            <div className="card-header">
              <h2 className="card-title">Contribution by member</h2>
            </div>
            <div style={{ overflowX: "auto" }}>
              <table className="data-table" style={{ width: "100%" }} data-testid="team-activity-table">
                <thead>
                  <tr>
                    <th style={{ textAlign: "left" }}>Member</th>
                    <th style={{ textAlign: "left" }}>Role</th>
                    <th style={{ textAlign: "right" }}>Leads added</th>
                    <th style={{ textAlign: "right" }}>Scans run</th>
                    <th style={{ textAlign: "right" }}>Credits</th>
                  </tr>
                </thead>
                <tbody>
                  {(activity?.by_member || []).map((m) => (
                    <tr key={m.username} data-testid={`activity-row-${m.username}`}>
                      <td>
                        <div style={{ fontWeight: 600, color: "#0f172a" }}>{m.display}</div>
                        <div style={{ fontSize: "12px", color: "#64748b" }}>{m.email}</div>
                      </td>
                      <td><Badge map={ROLE_STYLE} keyName={m.team_role} /></td>
                      <td style={{ textAlign: "right", fontWeight: 700, color: "#6d28d9" }}>{m.leads_added}</td>
                      <td style={{ textAlign: "right" }}>{m.scans_run}</td>
                      <td style={{ textAlign: "right", color: "#64748b" }}>{m.credits_spent}</td>
                    </tr>
                  ))}
                  {(!activity?.by_member || activity.by_member.length === 0) && (
                    <tr><td colSpan={5} style={{ textAlign: "center", color: "#94a3b8", padding: "24px" }}>No activity yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="content-card" data-testid="team-activity-recent">
            <div className="card-header">
              <h2 className="card-title">Recent leads added ({activity?.total_leads || 0} total)</h2>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
              {(activity?.recent_leads || []).map((l, i) => (
                <div key={i} data-testid={`activity-lead-${i}`} style={{
                  display: "flex", justifyContent: "space-between", alignItems: "center",
                  padding: "10px 14px", background: "#f8fafc", borderRadius: "10px", border: "1px solid #eef2f7",
                }}>
                  <div>
                    <div style={{ fontWeight: 600, color: "#0f172a" }}>{l.name}</div>
                    <div style={{ fontSize: "12px", color: "#64748b" }}>
                      {l.company || "—"}
                      {l.has_email && <span style={{ marginLeft: "8px", color: "#16a34a" }}>• email</span>}
                      {l.has_phone && <span style={{ marginLeft: "6px", color: "#2563eb" }}>• phone</span>}
                    </div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontSize: "13px", fontWeight: 600, color: "#6d28d9" }}>{l.added_by_display}</div>
                    <div style={{ fontSize: "11px", color: "#94a3b8" }}>
                      {l.updated_at ? new Date(l.updated_at).toLocaleDateString() : ""}
                    </div>
                  </div>
                </div>
              ))}
              {(!activity?.recent_leads || activity.recent_leads.length === 0) && (
                <div style={{ textAlign: "center", color: "#94a3b8", padding: "24px" }}>No leads added yet.</div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
